import { useMutation, useQueryClient } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION_KEY, DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { FilterRecord } from '@/modules/Reports/report.types';
import { useGetEmployerIdParam } from '@/modules/Reports/ReportDetails/hooks';

import { INFINITE_DATE, ScheduledReportPayload } from '../../ScheduledReport.types';
import { GET_SCHEDULERS_QUERY_KEY } from '../../ScheduledReports/queries/useGetScheduledReports.query';
import { useIsMultipleEmployerScheduledReport } from '../hooks';

interface Payload extends ScheduledReportPayload {
  filterValues: FilterRecord;
}
interface Params {
  id?: string;
  isEditMode?: boolean;
}
const useCreateScheduledReportQuery = ({ id, isEditMode }: Params) => {
  const queryClient = useQueryClient();
  const getEmployerIdParam = useGetEmployerIdParam();
  const { checkIsMultipleEmployerMode } = useIsMultipleEmployerScheduledReport();
  const { isLoading, mutateAsync } = useMutation(
    (payload: Payload) => {
      const filterValues = payload?.filterValues;
      const cardStatuses = _.get(filterValues, 'cardStatuses');
      const employmentStatuses = _.get(filterValues, 'employmentStatuses');
      const accountStatuses = _.get(filterValues, 'accountStatuses');
      const enrollmentStatuses = _.get(filterValues, 'enrollmentStatuses');
      const CIPStatuses = _.get(filterValues, 'CIPStatuses');
      const claimStatuses = _.get(filterValues, 'claimStatuses');
      const claimTypes = _.get(filterValues, 'claimTypes');
      const planYearPeriod = _.get(filterValues, 'planYearPeriod');
      const accountTypes = _.get(filterValues, 'accountTypes');
      const planIds = _.get(filterValues, 'planIds');
      const reportPlanIds = _.get(filterValues, 'reportPlanIds');
      const currentReportPlanIds = !_.isEmpty(reportPlanIds) ? reportPlanIds : planIds;
      const isMultipleEmployersMode = checkIsMultipleEmployerMode(payload.type);
      const employerId = !isMultipleEmployersMode
        ? {
            employer_id: filterValues.employerId,
          }
        : getEmployerIdParam(filterValues.employerId, filterValues.employerIds);
      const asDate = _.get(filterValues, 'asDate');

      const body = {
        name: payload.name,
        report_type: payload.type,
        report_group_type: payload.tab,
        cron: payload.cronString,
        start_date: payload.startDate
          ? dayjs(payload.startDate, DEFAULT_DATE_FORMAT).format(RAW_DATE_FORMAT)
          : undefined,
        end_date: payload.endDate
          ? dayjs(payload.endDate, DEFAULT_DATE_FORMAT).format(RAW_DATE_FORMAT)
          : INFINITE_DATE,

        report_filter: _.pickBy({
          ...employerId,

          search_value: _.get(filterValues, 'searchString'),
          show_ssn: _.get(filterValues, 'isSSNShowed'),
          card_status_report_group_type: _.get(filterValues, 'cardStatusGroupType'),
          as_of_date: asDate ? dayjs(asDate).format(RAW_DATE_FORMAT) : undefined,
          card_statuses: cardStatuses && !cardStatuses.includes(ALL_OPTION_KEY) ? cardStatuses : undefined,
          employment_statuses:
            employmentStatuses && !employmentStatuses.includes(ALL_OPTION_KEY)
              ? employmentStatuses
              : undefined,

          account_types: accountTypes && !accountTypes.includes(ALL_OPTION_KEY) ? accountTypes : undefined,
          account_statuses:
            accountStatuses && !accountStatuses.includes(ALL_OPTION_KEY) ? accountStatuses : undefined,

          enrollment_statuses:
            enrollmentStatuses && !enrollmentStatuses.includes(ALL_OPTION_KEY)
              ? enrollmentStatuses
              : undefined,

          cip_statuses: CIPStatuses && !CIPStatuses.includes(ALL_OPTION_KEY) ? CIPStatuses : undefined,
          claim_statuses:
            claimStatuses && !claimStatuses.includes(ALL_OPTION_KEY) ? claimStatuses : undefined,
          claim_types: claimTypes && !claimTypes.includes(ALL_OPTION_KEY) ? claimTypes : undefined,
          invoice_id: _.get(filterValues, 'invoiceID'),
          claim_activity_date_range_type: _.get(filterValues, 'activityDateRangeFor'),
          plan_year_period:
            planYearPeriod && !planYearPeriod.includes(ALL_OPTION_KEY) ? planYearPeriod : undefined,
          plan_ids:
            currentReportPlanIds && !currentReportPlanIds.includes(ALL_OPTION_KEY)
              ? currentReportPlanIds
              : undefined,
        }),
      };
      if (isEditMode) {
        return api.put(API_URL.REPORTS_SCHEDULERS_BY_ID(id || ''), body);
      }
      return api.post(API_URL.REPORTS_SCHEDULERS, body);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [GET_SCHEDULERS_QUERY_KEY],
        });
      },
    }
  );
  return {
    isLoading,
    handleSave: mutateAsync,
  };
};

export default useCreateScheduledReportQuery;
